import { FC, useMemo } from "react";

import { IEventGuestParams, IGuestlistTicketAndPass } from "Bands/types";
import { FormBuilder } from "shared/components";
import { IAPIState, IFormButtons, IAPIError } from "shared/types";

import { getFields } from "./formData";
import { MutedText, SmallText } from "shared/styles";
import { useBandContext } from "Bands/contexts";

interface GuestFormProps {
  defaultValues?: IEventGuestParams;
  buttons?: IFormButtons;
  handleSubmit: (data: IEventGuestParams) => void;
  apiStatus?: IAPIState | null;
  error?: boolean | string | JSX.Element;
  apiError?: IAPIError | null;
  preventSubmit?: boolean;
  ticketAndPasses: IGuestlistTicketAndPass[];
  maxTicketsPerEntry: number;
  hideStatus?: boolean;
  resetForm?: boolean;
}

export const GuestForm: FC<GuestFormProps> = ({
  defaultValues,
  buttons,
  handleSubmit,
  apiStatus,
  apiError,
  error,
  preventSubmit,
  ticketAndPasses,
  maxTicketsPerEntry,
  hideStatus,
  resetForm,
  children,
}) => {
  const { ticketAndPassTypes } = useBandContext();

  const options = useMemo(
    () =>
      ticketAndPasses
        .sort((a, b) => {
          const aIndex = ticketAndPassTypes.findIndex(type => type.id === a.ticketAndPassType.id);
          const bIndex = ticketAndPassTypes.findIndex(type => type.id === b.ticketAndPassType.id);

          return aIndex - bIndex;
        })
        .map(({ id, label, ticketsAvailable, ticketsTotal, isUnlimited }) => ({
          value: id,
          disabled: !isUnlimited && !ticketsAvailable,
          label: (
            <span>
              {label}
              <MutedText as={SmallText}>
                {" ・ "}
                {isUnlimited ? (
                  "unlimited"
                ) : (
                  <>
                    {ticketsAvailable}/{ticketsTotal} available
                  </>
                )}
              </MutedText>
            </span>
          ),
        })),
    [ticketAndPassTypes, ticketAndPasses]
  );

  return (
    <FormBuilder
      submitMode="isDirty"
      style={{
        gridTemplateColumns: "1fr 1fr 1fr 1fr",
      }}
      fields={getFields({
        hideStatus,
        maxTicketsPerEntry,
        ticketAndPasses: options,
      })}
      onSubmit={handleSubmit}
      defaultValues={defaultValues}
      buttons={buttons}
      isSubmitting={apiStatus === IAPIState.pending}
      apiErrorMessage={error}
      apiError={apiError}
      preventSubmit={preventSubmit}
      resetForm={resetForm}
    >
      {children}
    </FormBuilder>
  );
};
